import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    sendEmailVerification,
    sendPasswordResetEmail
} from "firebase/auth";

const FirebaseAuthContext = createContext();

export function useFirebaseAuth() {
    return useContext(FirebaseAuthContext);
}

export function FirebaseAuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    function loginWithGoogle() {
        const provider = new GoogleAuthProvider();
        return signInWithPopup(auth, provider);
    }

    function loginWithEmail(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    async function registerWithEmail(email, password) {
        const res = await createUserWithEmailAndPassword(auth, email, password);
        if (res.user) {
            try {
                await sendEmailVerification(res.user);
            } catch (err) {
                console.error("Error sending initial verification email:", err);
            }
        }
        return res;
    }

    function sendVerificationEmail(userToVerify) {
        const targetUser = userToVerify || auth.currentUser;
        if (!targetUser) throw new Error("Nenhum usuário autenticado para enviar e-mail de verificação.");
        return sendEmailVerification(targetUser);
    }

    function resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    }

    async function reloadUser() {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            setCurrentUser({ ...auth.currentUser });
        }
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const { doc, getDoc, setDoc, getFirestore, serverTimestamp } = await import("firebase/firestore");
                    const db = getFirestore();
                    const userRef = doc(db, "users", user.uid);
                    const userDoc = await getDoc(userRef);

                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        user.role = data.role || 'user';
                        user.trial_used = data.trial_used || false;
                        user.onboarding_skipped = data.onboarding_skipped || false;
                    } else {
                        const initialData = {
                            email: user.email,
                            role: 'user',
                            trial_used: false,
                            onboarding_skipped: false,
                            created_at: serverTimestamp()
                        };
                        await setDoc(userRef, initialData);
                        user.role = 'user';
                        user.trial_used = false;
                        user.onboarding_skipped = false;
                    }
                } catch (error) {
                    console.error("Error fetching/creating user role:", error);
                    user.role = 'user';
                }
            }
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        loading,
        loginWithGoogle,
        loginWithEmail,
        registerWithEmail,
        sendVerificationEmail,
        resetPassword,
        reloadUser,
        logout
    };

    return (
        <FirebaseAuthContext.Provider value={value}>
            {children}
        </FirebaseAuthContext.Provider>
    );
}
